import React, { useState, useEffect, useRef } from 'react';
import '../styles/governance_dashboard.css';
import api_url from "../config/api_url.json";

// Use the correct API URL from your config
const base_api_url = api_url.backend_api  // Note: using the correct key from your config

const GovernanceDashboard = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedForums, setSelectedForums] = useState(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProposal, setExpandedProposal] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 100;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = `${base_api_url}/api/data/governance-proposals`;
        console.log('Attempting to fetch from:', url);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          // Add these options to handle CORS
          mode: 'cors',
          credentials: 'same-origin'
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', [...response.headers.entries()]);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Received data:', data);

        if (!data || !data.proposals) {
          throw new Error('Invalid data format received');
        }

        const allProposals = Object.values(data.proposals || {})
          .flat()
          .map(proposal => ({
            ...proposal,
            created_at: new Date(proposal.created_at)
          }));

        setProposals(allProposals);
      } catch (err) {
        console.error('Detailed fetch error:', {
          message: err.message,
          stack: err.stack,
          type: err.name
        });
        setError(`Failed to fetch data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const uniqueForums = React.useMemo(() => {
    const forums = [...new Set(proposals.map(p => p.forum_name))];
    return forums.sort();
  }, [proposals]);

  const handleForumClick = (forum) => {
    setSelectedForums(prev => {
      if (forum === 'all') {
        return ['all'];
      }

      // If all was previously selected, remove it
      const withoutAll = prev.filter(f => f !== 'all');

      // Toggle the selected forum
      if (withoutAll.includes(forum)) {
        const filtered = withoutAll.filter(f => f !== forum);
        // If nothing is selected, default to 'all'
        return filtered.length === 0 ? ['all'] : filtered;
      }
      return [...withoutAll, forum];
    });
  };

  const { paginatedProposals, totalPages } = React.useMemo(() => {
    if (!proposals || proposals.length === 0) {
      return { paginatedProposals: [], totalPages: 0 };
    }

    try {
      let filteredProposals = proposals;
      if (!selectedForums.includes('all')) {
        filteredProposals = proposals.filter(p => selectedForums.includes(p.forum_name));
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredProposals = filteredProposals.filter(p =>
          p.title?.toLowerCase().includes(searchLower) ||
          p.forum_name?.toLowerCase().includes(searchLower) ||
          p.poster?.toLowerCase().includes(searchLower)
        );
      }

      const sortedProposals = [...filteredProposals].sort((a, b) => {
        if (!a || !b) return 0;

        switch (sortConfig.key) {
          case 'forum_name':
            return sortConfig.direction === 'asc'
              ? (a.forum_name || '').localeCompare(b.forum_name || '')
              : (b.forum_name || '').localeCompare(a.forum_name || '');

          case 'poster':
            return sortConfig.direction === 'asc'
              ? (a.poster || '').localeCompare(b.poster || '')
              : (b.poster || '').localeCompare(a.poster || '');

          case 'created_at':
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortConfig.direction === 'asc'
              ? dateA - dateB
              : dateB - dateA;

          default:
            return 0;
        }
      });

      const totalPages = Math.max(1, Math.ceil(sortedProposals.length / ITEMS_PER_PAGE));
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const paginatedProposals = sortedProposals.slice(startIndex, startIndex + ITEMS_PER_PAGE);

      return { paginatedProposals, totalPages };
    } catch (error) {
      console.error('Error in pagination calculation:', error);
      return { paginatedProposals: [], totalPages: 0 };
    }
  }, [proposals, sortConfig, selectedForums, searchTerm, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedForums]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  if (loading) return <div className="loading">Loading governance data...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="governance-dashboard">
      <h2 className="dashboard-title">Governance Proposals</h2>

      <div className="filter-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by title, forum, or poster..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="forum-filter" ref={dropdownRef}>
          <button
            className="forum-select-header"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            type="button"
          >
            {selectedForums.includes('all')
              ? 'All'
              : `${selectedForums.length} Forums Selected`}
            <span className="dropdown-arrow">▼</span>
          </button>
          {isDropdownOpen && (
            <div className="forum-dropdown-content">
              <div
                className="forum-option"
                onClick={() => handleForumClick('all')}
              >
                <input
                  type="checkbox"
                  checked={selectedForums.includes('all')}
                  readOnly
                />
                <span>All</span>
              </div>
              {uniqueForums.map(forum => (
                <div
                  key={forum}
                  className="forum-option"
                  onClick={() => handleForumClick(forum)}
                >
                  <input
                    type="checkbox"
                    checked={selectedForums.includes(forum)}
                    readOnly
                    disabled={selectedForums.includes('all')}
                  />
                  <span>{forum}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="governance-table-container">
        <table className="governance-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort('forum_name')}
                className="sortable"
              >
                Forum
                {sortConfig.key === 'forum_name' && (
                  <span className="sort-indicator">
                    {sortConfig.direction === 'desc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th
                onClick={() => handleSort('poster')}
                className="sortable"
              >
                Poster
                {sortConfig.key === 'poster' && (
                  <span className="sort-indicator">
                    {sortConfig.direction === 'desc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th className="title-column">Title</th>
              <th
                onClick={() => handleSort('created_at')}
                className="sortable"
              >
                Date
                {sortConfig.key === 'created_at' && (
                  <span className="sort-indicator">
                    {sortConfig.direction === 'desc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedProposals.length > 0 ? (
              paginatedProposals.map((proposal, index) => (
                <React.Fragment key={index}>
                  <tr
                    onClick={() => setExpandedProposal(expandedProposal === proposal.id ? null : proposal.id)}
                    className={`proposal-row ${expandedProposal === proposal.id ? 'expanded' : ''}`}
                  >
                    <td className="forum-cell">
                      {proposal.forum_image ? (
                        <img
                          src={proposal.forum_image}
                          alt={`${proposal.forum_name} logo`}
                          className="forum-logo"
                        />
                      ) : (
                        <div className="logo-placeholder"></div>
                      )}
                      <span>{proposal.forum_name}</span>
                    </td>
                    <td>{proposal.poster}</td>
                    <td>
                      <a
                        href={proposal.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {proposal.title}
                      </a>
                    </td>
                    <td>{formatDate(proposal.created_at)}</td>
                  </tr>
                  {expandedProposal === proposal.id && (
                    <tr className="proposal-content-row">
                      <td colSpan="4">
                        <div className="proposal-content">
                          <div className="proposal-body">
                            {proposal.body || "No description available"}
                          </div>
                          <div className="proposal-metadata">
                            <span className="source-type">Source: {proposal.source_type}</span>
                            <a
                              href={proposal.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="view-more-link"
                            >
                              View full proposal →
                            </a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>
                  {loading ? 'Loading...' : 'No proposals found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default GovernanceDashboard; 